// «Teléfonos» del portal del vendedor: el explorer de `portal-vendedor-catalogo.html` solo con
// equipos PHONE (los accesorios viven en su propia pantalla).
import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { MasterDetailExplorer } from '@/shared/patterns/MasterDetail';
import { ScreenFrame } from '@/shared/patterns/ScreenFrame';
import { useCatalog } from '@/domains/sales/wizard/api/useWizard';
import { useRecentSales } from './api/usePortal';
import { brandOptions, financierOptions } from './catalog/lib/catalogExplorer';
import { PhoneFiltersPanel } from './phones/components/PhoneFiltersPanel';
import { PhoneTable } from './phones/components/PhoneTable';
import { PhoneDetailPanel, type PhoneDetailTab } from './phones/components/PhoneDetailPanel';
import { useLevelPlans } from './phones/api/useLevelPlans';
import {
    DEFAULT_PHONE_SORT,
    EMPTY_PHONE_FILTERS,
    filterPhones,
    isPhone,
    phoneMetrics,
    phoneStats,
    sortPhones,
    type PhoneFilters,
    type PhoneSortState,
    type PhoneStatFilter,
} from './phones/lib/phoneCatalog';

export const PHONES_SECTION = 'phones';

export function SalesPhones({ sku }: { sku?: string } = {}) {
    const navigate = useNavigate();
    const [filters, setFilters] = useState<PhoneFilters>(EMPTY_PHONE_FILTERS);
    const [stat, setStat] = useState<PhoneStatFilter>('all');
    const [sort, setSort] = useState<PhoneSortState>(DEFAULT_PHONE_SORT);
    const [selectedSku, setSelectedSku] = useState<string | null>(sku ?? null);
    const [linkedSku, setLinkedSku] = useState(sku);
    const [tab, setTab] = useState<PhoneDetailTab>('producto');
    const [filtersCollapsed, setFiltersCollapsed] = useState(false);

    // El catálogo del portal es de solo lectura; el silo ignora el `sessionId`.
    const catalog = useCatalog('portal');
    const sales = useRecentSales();
    const { plans } = useLevelPlans();

    const phones = useMemo(() => (catalog.data?.items ?? []).filter(isPhone), [catalog.data]);

    // Llegar por un `?sku=` nuevo mueve la ficha.
    if (sku !== linkedSku) {
        setLinkedSku(sku);
        setSelectedSku(sku ?? null);
    }

    const rows = useMemo(
        () => sortPhones(filterPhones(phones, filters, stat, plans), sort),
        [phones, filters, stat, plans, sort],
    );
    const stats = useMemo(() => phoneStats(phones), [phones]);
    const metrics = useMemo(() => phoneMetrics(rows), [rows]);
    const brands = useMemo(() => brandOptions(phones), [phones]);
    const financiers = useMemo(() => financierOptions(phones), [phones]);
    const selected = phones.find((item) => item.sku === selectedSku) ?? null;

    const selectRow = (nextSku: string) => {
        setSelectedSku(nextSku);
        void navigate({
            to: '/sales/$section',
            params: { section: PHONES_SECTION },
            search: { sku: nextSku },
            replace: true,
        });
    };

    const startSale = (nextSku: string) => {
        void navigate({ to: '/wizard', search: { sku: nextSku } });
    };

    return (
        <ScreenFrame>
            <MasterDetailExplorer
                filtersCollapsed={filtersCollapsed}
                filters={
                    <PhoneFiltersPanel
                        filters={filters}
                        brands={brands}
                        financiers={financiers}
                        plans={plans}
                        onChange={setFilters}
                        collapsed={filtersCollapsed}
                        onToggleCollapsed={() => {
                            setFiltersCollapsed((value) => !value);
                        }}
                    />
                }
                list={
                    <PhoneTable
                        rows={rows}
                        stats={stats}
                        metrics={metrics}
                        stat={stat}
                        onStatChange={setStat}
                        sort={sort}
                        onSortChange={setSort}
                        selectedSku={selected?.sku ?? null}
                        onSelect={selectRow}
                        onSell={startSale}
                        isLoading={catalog.isLoading}
                        isError={catalog.isError}
                        onRetry={() => {
                            void catalog.refetch();
                        }}
                    />
                }
                detail={
                    <PhoneDetailPanel
                        item={selected}
                        phones={phones}
                        plans={plans}
                        sales={{ rows: sales.rows, isLoading: sales.isLoading, error: sales.error }}
                        tab={tab}
                        onTabChange={setTab}
                        onSell={startSale}
                        onSelectSku={selectRow}
                    />
                }
            />
        </ScreenFrame>
    );
}
